<script setup lang="ts">
import type { ExtensionPermission } from '#layers/extensions/types/catalog';
import type { PermissionCapability } from '#layers/extensions/utils/permissions';
import { computed } from 'vue';
import { groupPermissions, parsePermission } from '#layers/extensions/utils/permissions';

const {
	added: addedProp,
	permissions,
} = defineProps<{
	permissions: ExtensionPermission[];
	/** Permissions to mark as newly requested. */
	added?: string[];
}>();

// Each capability says in its own words what granting it allows.
const capabilityLeadKeys: Record<PermissionCapability, string> = {
	net: 'extensions.permissions.capabilities.net',
};

const groups = computed(() => {
	const added = new Set(addedProp);

	return groupPermissions(permissions).map(({ capability, permissions }) => ({
		key: capability ?? 'unknown',
		leadKey: capability ? capabilityLeadKeys[capability] : 'extensions.permissions.capabilities.unknown',
		items: permissions.map(({ permission, reason }) => ({
			id: permission,
			target: parsePermission(permission).value,
			reason,
			isAdded: added.has(permission),
		})),
	}));
});
</script>

<template>
	<div class="space-y-3">
		<div v-for="group in groups" :key="group.key" class="space-y-1">
			<p>{{ $t(group.leadKey) }}</p>
			<ul class="list-disc space-y-1 ps-5">
				<li v-for="item in group.items" :key="item.id">
					<span class="font-mono break-all text-default">{{ item.target }}</span>
					<span class="text-muted"> — {{ item.reason }}</span>
					<DBadge
						v-if="item.isAdded"
						class="ms-2 align-middle"
						color="primary"
						variant="subtle"
						size="sm"
						:label="$t('extensions.permissions.added')"
					/>
				</li>
			</ul>
		</div>
	</div>
</template>
